import { find, findIndex, forEach, get, includes, isEmpty, map } from 'lodash';

import { GRAPH_ELEMENTS, ENTRY_DATA_TYPES } from './graphic-elements';

import { GRAPH_STYLE_TYPES } from '../api/Graphics/Graphics';
import * as graphicOps from '../api/Graphics/graphic-operations';

export const DEFAULT_STYLE = {
  color: '#ea6512',
  colorRange: '',
  alpha: 0.9,
  weight: 1,
  borderAlpha: 0.9,
  borderColor: '#000'
};
const HORIZONTAL_PADDING = 15;
const NAV_BAR_HEIGHT = 56;
const HEADER_HEIGHT = 53; // When it is all in the same line
const TITLE_HEIGHT = 49;
const ALERT_HEIGHT = 66;
const LEFT_VERTICAL_HEIGHT = NAV_BAR_HEIGHT + HEADER_HEIGHT + TITLE_HEIGHT;
const RIGHT_VERTICAL_HEIGHT = LEFT_VERTICAL_HEIGHT + ALERT_HEIGHT;

/**
 * Gets the props for the Graphic React Element.
 *
 * @param {object} arg - The arguments object.
 * @param {number} arg.width - The width for the svg.
 * @param {number} arg.height - The height for the svg.
 * @param {object} arg.graphic - The graphic object.
 * @param {string[]} arg.attributes - The list of attributes of the layer.
 * @param {Array[]} arg.values - The list of values of each attribute of the layer.
 * @param {boolean} [arg.verticalPadding] - Flag to reduce the height or not.
 * @param {number} [arg.aspectRatio] - The fraction of the screen that the graphic occupies.
 * @param {number} [arg.isAtlas] - Flag to reduce the width or not.
 * @returns {object} The props.
 */
const getGraphicProps = ({
  width,
  height,
  graphic,
  attributes,
  values,
  verticalPadding = true,
  aspectRatio = 1,
  isAtlas = false
}) => {
  const numericProperties = graphicOps.getNumericProperties(graphic);
  const globalProperties = graphicOps.getGlobalProperties(graphic);
  const namedProperty = graphicOps.getNamedProperty(graphic);
  const graphicType = graphicOps.getType(graphic);
  const selectedElements = graphicOps.getSelectedElements(graphic);

  const graphicProperties = GRAPH_ELEMENTS.properties[graphicType];
  const { dataNumericType } = graphicProperties;

  const dataNumericArray = map(numericProperties, ({ property }) => {
    const index = findIndex(attributes, attribute => attribute === property);
    return map(values, valueArray => valueArray[index]);
  });
  const namedIndex = findIndex(attributes, attribute => attribute === namedProperty);
  const dataNamed = map(values, valueArray => `${valueArray[namedIndex]}`);

  const dataNumeric = includes(
    [ENTRY_DATA_TYPES.MULTIPLEVALUES, ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES],
    dataNumericType
  )
    ? dataNumericArray
    : dataNumericArray[0];

  let colorToUse;
  let styleType = '';
  let property = '';
  let styleData = DEFAULT_STYLE;
  let alpha;
  let weight;
  let borderColor;
  let borderAlpha;
  let numericName;
  if (!isEmpty(numericProperties)) {
    switch (dataNumericType) {
      case ENTRY_DATA_TYPES.MULTIPLECOLORS:
      case ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES:
        [{ styleType, styleData, property }] = numericProperties;
        if (styleType === GRAPH_STYLE_TYPES.SINGLE) {
          colorToUse = styleData.color;
        } else {
          colorToUse = [];
          forEach(dataNumeric, data => {
            colorToUse.push(get(find(styleData.legend, ['value', data]), 'color', '#fff'));
          });
        }
        ({ alpha, weight, borderColor, borderAlpha } = styleData);
        numericName = property;
        break;
      case ENTRY_DATA_TYPES.MULTIPLEVALUES:
      case ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES:
        alpha = [];
        weight = [];
        borderColor = [];
        borderAlpha = [];
        numericName = [];
        colorToUse = map(numericProperties, (numericProperty, index) => {
          ({ styleType, styleData, property } = numericProperty);
          alpha.push(styleData.alpha);
          weight.push(styleData.weight);
          borderColor.push(styleData.borderColor);
          borderAlpha.push(styleData.borderAlpha);
          numericName.push(property);

          if (styleType === GRAPH_STYLE_TYPES.SINGLE) {
            return styleData.color;
          }

          return map(dataNumeric[index], data =>
            get(find(styleData.legend, ['value', data]), 'color', '#fff')
          );
        });
        break;
      default:
        break;
    }
  }

  return {
    width: isAtlas ? width : (width - HORIZONTAL_PADDING * 2) * aspectRatio,
    height: verticalPadding ? height - RIGHT_VERTICAL_HEIGHT : height,
    dataNamed,
    dataNumeric,
    colorRange: colorToUse,
    alpha,
    weight,
    borderColor,
    borderAlpha,
    selectedElements,
    globalProperties,
    numericName
  };
};

export default getGraphicProps;
